import axios from 'axios';
import { setMaintenanceMode } from './components/MaintenanceMode';

// Import error modal function - will be undefined until ErrorModal component is loaded
let showError = null;

// Set up error modal function when ErrorModal component loads
if (typeof window !== 'undefined') {
  // Use setTimeout to ensure ErrorModal component is loaded
  setTimeout(() => {
    import('./components/ErrorModal').then(module => {
      showError = module.showError;
    }).catch(() => {
      // ErrorModal not available yet, will be set up when component loads
    });
  }, 100);
}

function looksLikeMaintenance(error) {
  const status = error.response?.status;
  if (status === 503) return true;
  // Render maintenance HTML sometimes comes back without a JSON body.
  const ct = String(error.response?.headers?.['content-type'] || '');
  if (status >= 500 && ct.includes('text/html')) {
    const body = error.response?.data;
    if (typeof body === 'string' && /maintenance/i.test(body)) return true;
  }
  return false;
}

// Add a response interceptor to handle errors
axios.interceptors.response.use(
  response => response,
  error => {
    if (looksLikeMaintenance(error)) {
      setMaintenanceMode(true);
      return Promise.reject(error);
    }

    // Don't intercept login/auth errors - let them be handled by the component
    const requestUrl = String(error.config?.url || '');
    const isAuthEndpoint = requestUrl.includes('/api/auth/login') || 
                          requestUrl.includes('/api/auth/request-account') ||
                          requestUrl.includes('/api/auth/change-password');
    const isPublicEndpoint = requestUrl.includes('/api/public/');
    
    if (!isAuthEndpoint && !isPublicEndpoint) {
      const errMsg = error.response?.data?.error || '';
      const isInvalidToken403 =
        error.response?.status === 403 &&
        (errMsg === 'Invalid token.' || errMsg.startsWith('Invalid or expired token'));

      if (error.response?.status === 401 || isInvalidToken403) {
        // 401 = auth failure; legacy servers returned 403 for invalid JWT — treat the same
        if (window.location.pathname !== '/login') {
          // Store the error temporarily
          sessionStorage.setItem('auth_error', JSON.stringify({
            message: errMsg || 'Session expired. Please log in again.',
            timestamp: Date.now()
          }));
          
          // Token expired or invalid
          localStorage.removeItem('token');
          localStorage.removeItem('permissions');
          localStorage.removeItem('mustChangePassword');
          localStorage.removeItem('user');
          localStorage.removeItem('timecards');
          Object.keys(localStorage)
            .filter((key) => key.startsWith('timecards:') || key.startsWith('timecardSchedules:'))
            .forEach((key) => localStorage.removeItem(key));
          sessionStorage.removeItem('permissionPreview');
          delete axios.defaults.headers.common['Authorization'];
          window.location.href = '/login';
        }
      } else if (error.response?.status === 403) {
        // Forced password change — keep session and let the app gate UI
        if (error.response?.data?.mustChangePassword) {
          localStorage.setItem('mustChangePassword', 'true');
          if (typeof window !== 'undefined' && typeof window.__setMustChangePassword === 'function') {
            window.__setMustChangePassword(true);
          }
          return Promise.reject(error);
        }

        // 403 = Permission denied
        const errorMessage = error.response?.data?.error || 'Permission denied';
        
        // Try to refresh permissions - they may have been updated
        // Only refresh if it's not an auth endpoint to avoid infinite loops
        if (!error.config?.url?.includes('/api/auth/refresh-permissions')) {
          // Trigger permission refresh if available
          if (typeof window !== 'undefined' && window.__refreshPermissions) {
            window.__refreshPermissions();
          }
        }
        
        // Use global error modal if available, or module function
        if (typeof window !== 'undefined' && window.__showError) {
          window.__showError(errorMessage);
        } else if (showError) {
          showError(errorMessage);
        }
      }
    }
    return Promise.reject(error);
  }
);

export default axios;
